<template>
  <teleport :to="props.teleportTo" :disabled="!props.teleportTo">
    <TransitionSlide :offset="[0, 70]" :duration="props.transitionDuration" easing="ease-in-out">
      <div v-if="_isVisible" :class="`srp-modal ${props.className}`" :style="{ zIndex: zIndex + 1 }" ref="domRefModalItself">
        <div
          :class="{
            'srp-modal__window': true,
            'srp-modal__window--shaking': isWindowShaking,
          }"
        >
          <slot name="insideWindow" />

          <div
            :class="{
              'srp-modal__window-in-wrap': true,
              'srp-modal__window-in-wrap--small': props.size === 'small',
              'srp-modal__window-in-wrap--medium': props.size === 'medium',
              'srp-modal__window-in-wrap--large': props.size === 'large',
            }"
            :style="_modalWidthStyle"
          >
            <div
              v-if="props.isCloseButtonVisible"
              :class="{
                'modal-close-button': true,
                'srp-modal__close-button': true,
              }"
              @click="() => hideModal()"
            >
              <IconEmbedded class="modal-close-button__icon" name="remove_4-5" />
            </div>

            <div v-if="slots.header" class="srp-modal__header"><slot name="header" /></div>
            <div
              :class="{
                'srp-modal__content-wrap': true,
                'srp-modal__content-wrap--with-top-curtain': props.isWithScroll && scrollPosition > 10,
              }"
            >
              <div
                v-if="slots.content"
                :class="{
                  'srp-modal__content': true,
                  'srp-modal__content--no-paddings': !props.isContentPaddings,
                  'srp-modal__content--with-scroll': props.isWithScroll,
                  'srp-modal__content--with-top-padding': !slots.header,
                }"
                :style="{ maxHeight: _maxHeight }"
                @scroll="$event => defineScrollPosition($event)"
                ref="domRefContent"
              >
                <slot name="content" :scrollToTheTop="scrollToTheTop" :closeModal="hideModal" />
              </div>
            </div>
            <div v-if="slots.footer" class="srp-modal__footer">
              <slot name="footer" :scrollToTheTop="scrollToTheTop" :closeModal="hideModal" />
            </div>
          </div>
        </div>

        <slot />
      </div>
    </TransitionSlide>

    <TransitionFade :duration="props.transitionDuration" easing="ease-in-out">
      <div
        v-if="_isVisible"
        class="bg-dim-layer"
        @click="hideModal(true)"
        :style="{
          zIndex: props.zIndex,
        }"
      ></div>
    </TransitionFade>
  </teleport>
</template>

<!-- prettier-ignore -->
<script lang="ts">export default { name: "SrpModal" };</script>

<script setup lang="ts">
import { ref, onMounted, watch, computed, useSlots, onBeforeUnmount, provide } from "vue";
import { acquireModalScrollLock, openedModalZindexList, releaseModalScrollLock } from "@stores/srpModalsStore";

// Components
import { TransitionSlide, TransitionFade } from "@morev/vue-transitions";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    isClosable?: boolean; // whether it should close on bg layer click
    size?: "small" | "medium" | "large";
    width?: number | string;
    className?: string;
    zIndex?: number;
    isWithScroll?: boolean;
    isContentPaddings?: boolean;
    maxHeight?: string;
    isCloseButtonVisible?: boolean;
    transitionDuration?: number;
    teleportTo?: string;
  }>(),
  {
    isVisible: false,
    isClosable: true, // whether modal should close by clicking on bg layer
    size: "medium",
    width: 0,
    className: "",
    zIndex: 80,
    isWithScroll: true,
    isContentPaddings: true,
    maxHeight: "",
    isCloseButtonVisible: true,
    transitionDuration: 150,
    teleportTo: "#app",
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
  (e: "close"): void;
}>();

const slots = useSlots();

// Calc maxHeight =============================================================
const _maxHeight = computed(() => {
  if (props.maxHeight) {
    return props.maxHeight;
  } else {
    // Simplified this to take up more of the screen by default (previously it left an annoying amount of space for short screens)
    // if (screenSize?.value === "mobile") return "calc(100dvh - 120px)";
    return "calc(100dvh - 120px)";
  }
});

// Makes width more flexible by accepting a string or a number
const _modalWidthStyle = computed((): Record<string, string> => {
  const w = props.width;
  if (w === undefined || w === null) {
    return {};
  }
  if (typeof w === "number") {
    if (!Number.isFinite(w) || w === 0) {
      return {};
    }
    return { width: `${w}px !important` };
  }
  const trimmed = String(w).trim();
  if (trimmed === "") {
    return {};
  }
  const n = Number(trimmed);
  if (Number.isFinite(n)) {
    return { width: `${n}px !important` };
  }
  return { width: `${trimmed} !important` };
});

// Show/hide modal ============================================================
const _isVisible = ref(false);

onMounted(() => {
  _isVisible.value = props.isVisible;
});

let _isVisibleTimeout = null;
let updateIsVisibleTimeout = null;
let closeTimeout = null;
let scrollLockReleaseTimeout = null;
let hasAcquiredScrollLock = false;

function updateScrollLock(visible: boolean): void {
  clearTimeout(scrollLockReleaseTimeout);
  if (visible) {
    if (!hasAcquiredScrollLock) {
      acquireModalScrollLock();
      hasAcquiredScrollLock = true;
    }
  } else if (hasAcquiredScrollLock) {
    scrollLockReleaseTimeout = setTimeout(() => {
      releaseModalScrollLock();
      hasAcquiredScrollLock = false;
    }, props.transitionDuration);
  }
}

watch(_isVisible, updateScrollLock);

watch(
  () => props.isVisible,
  () => {
    clearTimeout(_isVisibleTimeout);
    clearTimeout(updateIsVisibleTimeout);
    clearTimeout(closeTimeout);

    _isVisible.value = props.isVisible;
  }
);

function hideModal(isClickOnBgLayer?: boolean, isForce?: boolean): void {
  if (isClickOnBgLayer && !props.isClosable && !isForce) {
    shakeWindow();
    return;
  }
  _isVisible.value = false;

  updateIsVisibleTimeout = setTimeout(() => emit("update:isVisible", false), props.transitionDuration);
  emit("close");
}

// Shake window when not closable =============================================
const isWindowShaking = ref(false);
let shakingTimeout = null;

function shakeWindow() {
  clearTimeout(shakingTimeout);

  isWindowShaking.value = true;
  shakingTimeout = setTimeout(() => (isWindowShaking.value = false), 200);
}

// Define scroll position =====================================================
const scrollPosition = ref(0);

onMounted(defineScrollPosition);

function defineScrollPosition(event?: Event): void {
  scrollPosition.value = (event?.target as Element)?.scrollTop || 0;
}

// ============================================================================
// Provide the scrollPosition to catch in inside the slots and recalculate
// the SrpDropdown position or for any other purpose
provide("srpModalScrollTop", scrollPosition);

// Scroll to the top ==========================================================
const domRefContent = ref(null);

function scrollToTheTop() {
  domRefContent.value.scrollTop = 0;
}

// Set domRefs ================================================================
const domRefModalItself = ref<HTMLElement | null>(null);

// Add the Modal "id" to the srpModals store ==================================
onMounted(() => openedModalZindexList.push(props.zIndex));

onBeforeUnmount(() => {
  clearTimeout(scrollLockReleaseTimeout);
  if (hasAcquiredScrollLock) {
    releaseModalScrollLock();
    hasAcquiredScrollLock = false;
  }

  const currentZindexIndex = openedModalZindexList.findIndex(zi => zi === props.zIndex);
  openedModalZindexList.splice(currentZindexIndex, 1);
});

// Handle Esc press ===========================================================
function handleEscPress(event: KeyboardEvent) {
  if (event.key === "Escape") {
    // don't close the modal if it's zIndex is not highest
    if (Math.max(...openedModalZindexList) !== props.zIndex) {
      return;
    }
    hideModal(!props.isClosable);
  }
}

onMounted(() => {
  window.addEventListener("keydown", handleEscPress);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleEscPress);
});

// Define expose ==============================================================
defineExpose({ domRefModalItself, domRefContent });
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/modal-close-button.scss";
@import "@/scss/variables.scss";

// Sherpa modal ===============================================================
.srp-modal {
  width: 1px;
  height: 1px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  inset: 50dvh auto auto 50vw;

  &__window {
    max-width: calc(100vw - 40px);
    border-radius: 5px;
    position: relative;
    transform-origin: center center;
    background: rgba(255, 255, 255, 1);
    border: 1px solid rgba(0, 0, 0, 0.12);
    box-shadow:
      0 8px 40px rgba(0, 0, 0, 0.45),
      0 -4px 20px rgba(0, 0, 0, 0.25);

    &--shaking {
      /* @keyframes duration | easing-function | delay | iteration-count | direction | fill-mode | play-state | name */
      animation: 170ms ease-in-out 0s 1 reverse both running horizontal-shaking;
    }

    @keyframes horizontal-shaking {
      0% {
        transform: translateX(0);
      }
      16% {
        transform: rotate(0.3deg);
      }
      33% {
        transform: rotate(-0.7deg);
      }
      50% {
        transform: rotate(1deg);
      }
      66% {
        transform: rotate(-0.7deg);
      }
      83% {
        transform: rotate(0.3deg);
      }
      100% {
        transform: translateX(0);
      }
    }
  }

  &__window-in-wrap {
    max-width: calc(100vw - 40px);

    &--small {
      width: 450px;
    }
    &--medium {
      width: 680px;
    }
    &--large {
      width: 800px;
    }
  }

  &__close-button {
    position: absolute;
    inset: -8px -8px auto auto;
    z-index: 11;
  }

  &__header {
    padding: 20px 30px 20px;
  }

  &__content-wrap {
    position: relative;

    &::before {
      content: "";
      width: calc(100% - 15px);
      height: 22px;
      border-radius: 6px;
      position: absolute;
      inset: 0 auto auto 0;
      z-index: 10;
      background: linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.07s ease-in-out;
    }

    &--with-top-curtain {
      &::before {
        opacity: 1;
      }
    }
  }

  &__content {
    @include standardFont;
    width: calc(100% - 5px);
    padding: 0 25px 40px 30px;
    //scroll-behavior: smooth;

    &--with-scroll {
      position: relative;
      overflow-x: hidden;
      overflow-y: scroll;

      &::-webkit-scrollbar {
        width: 10px !important;
      }

      &::-webkit-scrollbar-track {
        background: white;
      }

      &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.25);
      }

      &::-webkit-scrollbar-thumb:hover {
        background-color: rgba(0, 0, 0, 0.35);
      }
    }

    &--with-top-padding {
      padding-top: 20px;
    }

    &--no-paddings {
      padding: 0 !important;
      margin: 0 !important;
    }
  }

  &__footer {
    gap: 12px;
    padding: 20px 30px;
    border-radius: 0 0 7px 7px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
    background: rgba(223, 223, 223, 0.3);
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .srp-modal {
    top: 50dvh;

    &__window {
      max-width: calc(100vw - 30px);
    }

    &__header {
      padding: 17px 20px 16px;
    }

    &__content {
      width: 100%;
      padding: 0 10px 10px 20px;

      &--with-top-padding {
        padding-top: 13px;
      }

      &--no-paddings {
        padding: 0 !important;
        margin: 0 !important;
      }
    }

    &__footer {
      padding: 16px 20px;
    }
  }
}

// Bg dim layer ===============================================================
.bg-dim-layer {
  width: 120vw;
  height: 100dvh;
  position: fixed;
  inset: 0 auto auto 0;
  background: rgba(2, 7, 10, 0.9);
  mix-blend-mode: hard-light;
}
</style>
